import { useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from '../components/WebSocketContext';
import { useActionScope } from '../components/Actions/ActionScopeContext';
import { isActionEnabled, buildGetExportSettingsPayload } from '../Utils/actionSettingsUtils';

/**
 * Unified hook for managing action settings across all action components
 * Handles common functionality like enable/disable, conditions, save/force run, button states
 */
export const useActionSettings = ({
  settingId,
  caseid,
  actionType,
  onSaveStatusChange,
  customComparison = () => true, // Custom function to compare additional fields
  customSaveData = () => ({}), // Custom function to generate additional save data
  loadDataFunction = null, // Custom function to load data
  enableConditions = true,
  initialScope = 'event' // Initial scope for new actions
}) => {
  const currentScopeFromContext = useActionScope() || initialScope || 'event';
  // Common state
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [id, setId] = useState(settingId || 0);
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunResult, setForceRunResult] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [forceRunWarningOpen, setForceRunWarningOpen] = useState(false);
  const [forceRunWarningCount, setForceRunWarningCount] = useState(0);
  const [pendingForceRunCheck, setPendingForceRunCheck] = useState(false);
  
  const initialSettings = useRef({});
  const { ws, subscribeToMessages } = useWebSocket();

  // Initialize settings
  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');
    
    if (ws && settingId && !isTemporaryId) {
      ws.send(JSON.stringify(buildGetExportSettingsPayload(settingId, caseid)));
    } else if (isTemporaryId) {
      setId(settingId);
      setIsEnabled(false);
      setConditions([]);
      
      const defaultData = {
        name: `New ${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Flow`
      };
      
      initialSettings.current = {
        id: settingId,
        enable: false,
        useConditions: enableConditions,
        conditions: [],
        scope: currentScopeFromContext || initialScope || 'event',
        data: defaultData
      };
      setIsSaveDisabled(false);
    }
  }, [ws, settingId, caseid, actionType, enableConditions, currentScopeFromContext, initialScope]);

  // WebSocket message handler
  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        if (parsedMessage.command === 'getExportSettings' && parsedMessage.data.type === actionType) {
          console.log('Received export settings:', parsedMessage.data);
          const { id: newId, enable, customData, scope } = parsedMessage.data;
          setId(newId);
          setIsEnabled(isActionEnabled(enable));
          
          if (enableConditions && customData?.conditions !== undefined) {
            setConditions(customData.conditions);
          }

          initialSettings.current = {
            id: newId,
            enable: isActionEnabled(enable),
            useConditions: enableConditions,
            conditions: customData?.conditions || [],
            scope: scope || currentScopeFromContext || initialScope || 'event',
            ...customData
          };
          
          // Call custom load function if provided
          if (loadDataFunction && parsedMessage.data) {
            loadDataFunction(parsedMessage.data);
          }
          
          setIsSaveDisabled(true);
        } else if (parsedMessage.command === 'forceRun') {
          if (parsedMessage.data && parsedMessage.data.type === actionType) {
            setForceRunResult({
              total: parsedMessage.data.total,
              success: parsedMessage.data.success,
              skipped: parsedMessage.data.skipped,
              fail: parsedMessage.data.fail,
              files_failed: parsedMessage.data.files_failed,
              failure_details: parsedMessage.data.failure_details || []
            });
            setForceRunLoading(false);
            setPopUpOpen(true);
          }
        } else if (parsedMessage.command === 'getTimeeventCount') {
          if (pendingForceRunCheck && String(parsedMessage.data?.caseid) === String(caseid)) {
            setPendingForceRunCheck(false);
            const total = parsedMessage.data?.total || 0;
            if (total > 10) {
              setForceRunWarningCount(total);
              setForceRunWarningOpen(true);
              setForceRunLoading(false);
            } else {
              ws.send(JSON.stringify({
                command: 'forceRun',
                caseid,
                export_id: id,
              }));
            }
          }
        } else if (parsedMessage.command === 'saveActionSettings') {
          if (parsedMessage.data && parsedMessage.data.id && parsedMessage.data.type === actionType) {
            const savedId = parsedMessage.data.id;
            const savedCustomData = parsedMessage.data.customData;
            
            setId(savedId);
            initialSettings.current = {
              id: savedId,
              enable: isEnabled,
              useConditions: enableConditions,
              conditions: [...conditions],
              scope: parsedMessage.data.scope || currentScopeFromContext || initialScope || 'event',
              data: savedCustomData || initialSettings.current.data || {},
              ...customSaveData(savedCustomData)
            };
            
            // Immediately disable save button and enable run button
            setIsSaveDisabled(true);
            
            if (onSaveStatusChange) {
              onSaveStatusChange(savedId, false, typeof id === 'string' && id.startsWith('temp_') ? id : null);
            }
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, isEnabled, conditions, onSaveStatusChange, id, actionType, enableConditions, customSaveData, loadDataFunction, pendingForceRunCheck, caseid, ws]);

  // Update save disabled state
  const updateSaveDisabledState = useCallback(() => {
    if (initialSettings.current && initialSettings.current.id !== undefined) {
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);
      const currentConditionsJSON = JSON.stringify(conditions);

      const commonUnchanged = 
        isEnabled === initialSettings.current.enable &&
        (!enableConditions || (
          initialSettings.current.useConditions === enableConditions &&
          currentConditionsJSON === initialConditionsJSON
        ));

      const customUnchanged = customComparison(initialSettings.current);
      const unchanged = commonUnchanged && customUnchanged;

      const hasChanges = !unchanged;
      
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasChanges);
      }
      setIsSaveDisabled(unchanged);
    }
  }, [isEnabled, conditions, id, onSaveStatusChange, customComparison, enableConditions]);

  useEffect(() => {
    updateSaveDisabledState();
  }, [isEnabled, conditions, updateSaveDisabledState]);

  // Handle conditions change
  const handleConditionsChange = useCallback((newConditions) => {
    setConditions(newConditions);
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    if (ws) {
      const scopeToPersist = currentScopeFromContext || initialScope || 'event';
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : initialSettings.current.id;
      const isNewAction = typeof currentId === 'string' && currentId.startsWith('temp_');
      
      // Get custom data from the customSaveData function
      let customData = {};
      try {
        customData = customSaveData() || {};
        // Ensure customData is serializable
        JSON.stringify(customData);
      } catch (error) {
        console.error('Error getting or serializing custom save data:', error);
        customData = {};
      }
      
      const baseSettingsData = {
        ...(enableConditions && {
          useConditions: true,
          conditions
        }),
        ...customData
      };
      
      try {
        const saveMessage = {
          command: 'saveActionSettings',
          id: currentId,
          caseid,
          type: actionType,
          enable: isEnabled ? '1' : '0',
          interval: '0',
          data: baseSettingsData,
          scope: scopeToPersist
        };
        
        ws.send(JSON.stringify(saveMessage));
      } catch (error) {
        console.error('Error sending save action settings:', error);
        throw error;
      }
    }
  }, [ws, isEnabled, conditions, caseid, actionType, id, enableConditions, customSaveData, currentScopeFromContext, initialScope]);

  // Handle force run
  const handleForceRun = useCallback(() => {
    if (ws) {
      const scopeToUse = initialSettings.current?.scope || currentScopeFromContext || initialScope || 'event';
      if (scopeToUse === 'event') {
        setForceRunLoading(true);
        setPendingForceRunCheck(true);
        ws.send(JSON.stringify({
          command: 'getTimeeventCount',
          caseid
        }));
      } else {
        setForceRunLoading(true);
        ws.send(JSON.stringify({
          command: 'forceRun',
          caseid,
          export_id: id,
        }));
      }
    }
  }, [ws, caseid, id, currentScopeFromContext, initialScope]);

  const handleForceRunConfirm = useCallback((mode) => {
    if (!ws) {
      return;
    }
    const payload = {
      command: 'forceRun',
      caseid,
      export_id: id,
    };

    if (mode === 'first_event_only') {
      payload.timeevent_limit = 1;
    }

    setForceRunWarningOpen(false);
    setForceRunLoading(true);
    ws.send(JSON.stringify(payload));
  }, [ws, caseid, id]);

  const handleForceRunCancel = useCallback(() => {
    setForceRunWarningOpen(false);
    setForceRunLoading(false);
  }, []);

  // Close status window
  const handleCloseStatusWindow = useCallback(() => {
    setPopUpOpen(false);
    setForceRunResult(null);
  }, []);

  return {
    // State
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    id,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    initialSettings,

    // Handlers
    handleConditionsChange,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState
  };
};

export default useActionSettings;

